import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { getMessaging, getToken, isSupported } from "firebase/messaging";
import { db, firebaseApp } from "./firebase";

export type NotificationOptInResult = "enabled" | "denied" | "unsupported" | "configuration-required";

async function tokenDocumentId(token: string) {
  const bytes = new TextEncoder().encode(token);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest)).map((value) => value.toString(16).padStart(2, "0")).join("");
}

export async function enableBriefNotifications(userId: string): Promise<NotificationOptInResult> {
  if (!(await isSupported()) || !("serviceWorker" in navigator) || !("Notification" in window)) return "unsupported";
  const vapidKey = String(import.meta.env.VITE_FIREBASE_VAPID_KEY || "").trim();
  if (!vapidKey) return "configuration-required";
  const permission = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
  if (permission !== "granted") return "denied";
  const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
  const token = await getToken(getMessaging(firebaseApp), { vapidKey, serviceWorkerRegistration: registration });
  if (!token) return "configuration-required";
  const deviceId = await tokenDocumentId(token);
  await setDoc(doc(db, "users", userId, "notificationDevices", deviceId), {
    token,
    platform: "web",
    enabled: true,
    userAgent: navigator.userAgent.slice(0, 300),
    updatedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
  }, { merge: true });
  return "enabled";
}

export async function disableBriefNotifications(userId: string) {
  if (!(await isSupported()) || Notification.permission !== "granted") return;
  const vapidKey = String(import.meta.env.VITE_FIREBASE_VAPID_KEY || "").trim();
  if (!vapidKey) return;
  const registration = await navigator.serviceWorker.getRegistration("/firebase-messaging-sw.js") ?? await navigator.serviceWorker.getRegistration();
  if (!registration) return;
  const token = await getToken(getMessaging(firebaseApp), { vapidKey, serviceWorkerRegistration: registration });
  if (!token) return;
  await setDoc(doc(db, "users", userId, "notificationDevices", await tokenDocumentId(token)), { enabled: false, updatedAt: serverTimestamp() }, { merge: true });
}

export function notificationOptInMessage(result: NotificationOptInResult) {
  if (result === "enabled") return "Browser notifications are on.";
  if (result === "denied") return "Browser notifications are off. Completion alerts will still appear inside the app.";
  if (result === "unsupported") return "This browser does not support push notifications. Completion alerts will still appear inside the app.";
  return "Web push needs its Firebase Web Push key. Completion alerts will still appear inside the app.";
}
